import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { IContactCollectionSurveyOptions, IContactFieldOptions } from '../../../../../../extViewmodels';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components-v2/Select';
import { FormFieldWrapper } from '../FormFieldWrapper';
import { styleSheet } from './styles';

/** The contact-collection slice of a survey-creation form; every survey type that collects contact info shares it. */
export interface IContactCollectionFormValues {
	options?: IContactCollectionSurveyOptions;
}

type ContactOptionsFieldName = keyof NonNullable<IContactCollectionFormValues['options']>;

const getDropdownText = (collect: boolean, require: boolean): string => {
	if (require && collect) return 'Required';
	if (collect && !require) return 'Collect';
	return 'Do Not Collect';
};

const toContactFieldOptions = (value: string, currentUpdateExisting = false): IContactFieldOptions => {
	switch (value) {
		case 'Required':
			return { collect: true, require: true, updateExisting: currentUpdateExisting };
		case 'Collect':
			return { collect: true, require: false, updateExisting: currentUpdateExisting };
		case 'Do Not Collect':
		default:
			return { collect: false, require: false, updateExisting: currentUpdateExisting };
	}
};

const CONTACT_FIELDS: { fieldName: ContactOptionsFieldName; title: string; description: string; triggerId: string }[] =
	[
		{
			fieldName: 'contactNameOptions',
			title: 'Contact Name',
			description: 'Collect first and last name from respondents',
			triggerId: 'contact-name-dropdown',
		},
		{
			fieldName: 'contactEmailOptions',
			title: 'Contact Email',
			description: 'Collect email address from respondents',
			triggerId: 'contact-email-dropdown',
		},
		{
			fieldName: 'contactPhoneNumberOptions',
			title: 'Contact Phone Number',
			description: 'Collect phone number from respondents',
			triggerId: 'contact-phone-dropdown',
		},
		{
			fieldName: 'contactBirthdayOptions',
			title: 'Contact Birthday',
			description: 'Collect birthday from respondents',
			triggerId: 'contact-birthday-dropdown',
		},
		{
			fieldName: 'contactAddressOptions',
			title: 'Contact Address',
			description: 'Collect mailing address from respondents',
			triggerId: 'contact-address-dropdown',
		},
	];

/**
 * The five Do Not Collect / Collect / Required dropdowns for respondent contact info (name, email,
 * phone, birthday, address), bound to `options.contact*Options` on the enclosing survey-creation form.
 * `descriptions` lets a survey type replace the generic per-field copy (e.g. to say which respondents
 * actually see the fields).
 */
export const ContactCollectionFields = ({
	disabled = false,
	descriptions,
}: {
	disabled?: boolean;
	descriptions?: Partial<Record<ContactOptionsFieldName, string>>;
}) => {
	const { control, watch, setValue } = useFormContext<IContactCollectionFormValues>();
	const options = watch('options');

	return (
		<>
			{CONTACT_FIELDS.map(({ fieldName, title, description, triggerId }) => {
				const fieldOptions = options?.[fieldName];
				const currentSelection = getDropdownText(fieldOptions?.collect ?? false, fieldOptions?.require ?? false);

				return (
					<FormFieldWrapper
						key={fieldName}
						name={`options.${fieldName}`}
						control={control}
						title={title}
						description={descriptions?.[fieldName] ?? description}
						textWrapperStyles={[styleSheet.textWrapper]}
					>
						{() => (
							<Select
								value={currentSelection}
								disabled={disabled}
								onValueChange={(value: string) => {
									setValue(
										`options.${fieldName}`,
										toContactFieldOptions(value, fieldOptions?.updateExisting ?? false),
										{
											shouldDirty: true,
										}
									);
								}}
							>
								<SelectTrigger id={triggerId} styles={[styleSheet.selectTrigger]}>
									<SelectValue placeholder='Select an option'>{currentSelection}</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='Do Not Collect'>Do Not Collect</SelectItem>
									<SelectItem value='Collect'>Collect</SelectItem>
									<SelectItem value='Required'>Required</SelectItem>
								</SelectContent>
							</Select>
						)}
					</FormFieldWrapper>
				);
			})}
		</>
	);
};
